import { useEffect, useRef, useState, useCallback } from 'react';
import { signalingService } from '../firebase/realtimeSignaling';

/**
 * Improved WebRTC hook with better connection handling
 */
export const useWebRTC = (classId, userId, localStream, participants) => {
  const [remoteStreams, setRemoteStreams] = useState({});
  const peersRef = useRef({});
  const signalingUnsubscribeRef = useRef(null);
  const processedSignalsRef = useRef(new Set());
  const connectionStateRef = useRef({});

  // ICE servers configuration
  const iceServers = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ];

  const createPeerConnection = useCallback(async (remoteUserId, isInitiator = false) => {
    if (!classId || !userId) {
      console.log('[WebRTC] Cannot create peer - missing classId or userId');
      return;
    }

    // Prevent duplicate peer connections
    if (peersRef.current[remoteUserId]) {
      console.log(`[WebRTC] Peer connection already exists for ${remoteUserId}`);
      return peersRef.current[remoteUserId];
    }

    try {
      const Peer = (await import('simple-peer')).default;
      
      console.log(`[WebRTC] Creating peer for ${remoteUserId}, initiator: ${isInitiator}`);
      
      const peerConfig = {
        initiator: isInitiator,
        trickle: true, // Enable trickle ICE for faster connections
        config: { iceServers }
      };
      
      // Add local stream if available
      if (localStream) {
        peerConfig.stream = localStream;
        console.log(`[WebRTC] Adding local stream with ${localStream.getTracks().length} tracks`);
      } else {
        console.log(`[WebRTC] No local stream - receive-only mode`);
      }
      
      const peer = new Peer(peerConfig);
      
      // Track connection state
      connectionStateRef.current[remoteUserId] = {
        state: 'connecting',
        lastSignal: Date.now()
      };

      // Handle signaling data (offers, answers, ICE candidates)
      peer.on('signal', async (data) => {
        try {
          const type = data.type || 'ice-candidate';
          console.log(`[WebRTC] 📤 Sending ${type} to ${remoteUserId}`);
          
          await signalingService.sendSignaling(
            classId,
            userId,
            remoteUserId,
            type,
            { sdp: JSON.stringify(data) }
          );
          
          connectionStateRef.current[remoteUserId].lastSignal = Date.now();
        } catch (error) {
          console.error(`[WebRTC] ❌ Error sending signal to ${remoteUserId}:`, error);
        }
      });

      // Handle incoming stream
      peer.on('stream', (stream) => {
        console.log(`[WebRTC] ✅ Received stream from ${remoteUserId}`, {
          id: stream.id,
          tracks: stream.getTracks().length,
          video: stream.getVideoTracks().length,
          audio: stream.getAudioTracks().length
        });
        
        setRemoteStreams(prev => ({
          ...prev,
          [remoteUserId]: stream
        }));
        
        connectionStateRef.current[remoteUserId].state = 'streaming';
      });

      // Handle connection
      peer.on('connect', () => {
        console.log(`[WebRTC] ✅ Connected to ${remoteUserId}`);
        connectionStateRef.current[remoteUserId].state = 'connected';
      });

      // Handle errors
      peer.on('error', (err) => {
        console.error(`[WebRTC] ❌ Peer error with ${remoteUserId}:`, err);
        connectionStateRef.current[remoteUserId].state = 'error';
        
        // Attempt reconnection after error
        setTimeout(() => {
          if (peersRef.current[remoteUserId] === peer) {
            console.log(`[WebRTC] Attempting to reconnect to ${remoteUserId}`);
            cleanupPeer(remoteUserId);
            createPeerConnection(remoteUserId, isInitiator);
          }
        }, 3000);
      });

      // Handle close
      peer.on('close', () => {
        console.log(`[WebRTC] Connection closed with ${remoteUserId}`);
        cleanupPeer(remoteUserId);
      });

      peersRef.current[remoteUserId] = peer;
      console.log(`[WebRTC] ✅ Peer created for ${remoteUserId}`);
      
      return peer;
    } catch (error) {
      console.error(`[WebRTC] Error creating peer for ${remoteUserId}:`, error);
      return null;
    }
  }, [localStream, classId, userId]);

  // Cleanup a specific peer
  const cleanupPeer = useCallback((remoteUserId) => {
    const peer = peersRef.current[remoteUserId];
    if (peer && !peer.destroyed) {
      try {
        peer.destroy();
      } catch (err) {
        console.error(`[WebRTC] Error destroying peer ${remoteUserId}:`, err);
      }
    }
    
    delete peersRef.current[remoteUserId];
    delete connectionStateRef.current[remoteUserId];
    
    setRemoteStreams(prev => {
      const updated = { ...prev };
      delete updated[remoteUserId];
      return updated;
    });
  }, []);

  // Set up signaling subscription
  useEffect(() => {
    if (!classId || !userId) return;

    console.log('[WebRTC] Setting up signaling subscription');

    signalingUnsubscribeRef.current = signalingService.subscribeToSignaling(
      classId,
      userId,
      async (message) => {
        try {
          const { fromUserId, type, sdp } = message;
          
          // Create unique signal key for deduplication
          const signalKey = `${fromUserId}_${type}_${Date.now()}`;
          if (processedSignalsRef.current.has(signalKey)) {
            console.log(`[WebRTC] ⏭️ Skipping duplicate signal from ${fromUserId}`);
            return;
          }
          processedSignalsRef.current.add(signalKey);
          
          // Clean up old processed signals
          if (processedSignalsRef.current.size > 50) {
            const keys = Array.from(processedSignalsRef.current);
            keys.slice(0, 25).forEach(k => processedSignalsRef.current.delete(k));
          }

          console.log(`[WebRTC] 📥 Received ${type} from ${fromUserId}`);

          if (!sdp) {
            console.warn('[WebRTC] Missing SDP in signal');
            return;
          }

          // Parse SDP
          let signalData;
          try {
            signalData = typeof sdp === 'string' ? JSON.parse(sdp) : sdp;
          } catch (e) {
            console.error('[WebRTC] Failed to parse SDP:', e);
            return;
          }

          // Get or create peer
          let peer = peersRef.current[fromUserId];
          
          if (type === 'offer') {
            // Received offer - create answering peer if doesn't exist
            if (!peer) {
              console.log(`[WebRTC] Creating answering peer for ${fromUserId}`);
              peer = await createPeerConnection(fromUserId, false);
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } else if (type === 'answer') {
            // Received answer - peer should already exist
            if (!peer) {
              console.warn(`[WebRTC] Received answer but no peer exists for ${fromUserId}`);
              return;
            }
          }

          // Signal the peer
          if (peer && !peer.destroyed) {
            try {
              peer.signal(signalData);
              console.log(`[WebRTC] ✅ Signaled peer for ${fromUserId}`);
            } catch (err) {
              console.error(`[WebRTC] Error signaling peer:`, err);
            }
          }
        } catch (error) {
          console.error('[WebRTC] Error handling signaling message:', error);
        }
      }
    );

    return () => {
      console.log('[WebRTC] Cleaning up signaling subscription');
      if (signalingUnsubscribeRef.current) {
        signalingUnsubscribeRef.current();
        signalingUnsubscribeRef.current = null;
      }
    };
  }, [classId, userId, createPeerConnection]);

  // Handle participants changes
  useEffect(() => {
    if (!participants.length || !userId) return;

    console.log(`[WebRTC] Processing ${participants.length} participants`);

    // Create connections for new participants
    participants.forEach(async (participant) => {
      const participantId = participant.userId || participant.id;
      
      if (participantId === userId) return; // Skip self
      
      if (!peersRef.current[participantId]) {
        // Determine who initiates based on ID comparison
        const isInitiator = userId < participantId;
        console.log(`[WebRTC] Creating connection to ${participantId}, initiator: ${isInitiator}`);
        await createPeerConnection(participantId, isInitiator);
      }
    });

    // Remove connections for participants who left
    const currentParticipantIds = participants.map(p => p.userId || p.id);
    Object.keys(peersRef.current).forEach(peerId => {
      if (!currentParticipantIds.includes(peerId)) {
        console.log(`[WebRTC] Removing peer ${peerId} (left)`);
        cleanupPeer(peerId);
      }
    });
  }, [participants, userId, createPeerConnection, cleanupPeer]);

  // Update peer connections when local stream changes
  useEffect(() => {
    if (!localStream) return;

    console.log('[WebRTC] Local stream updated, updating peer connections');

    Object.entries(peersRef.current).forEach(([remoteUserId, peer]) => {
      if (!peer || peer.destroyed || !peer._pc) return;

      try {
        const senders = peer._pc.getSenders();
        const videoTrack = localStream.getVideoTracks()[0];
        const audioTrack = localStream.getAudioTracks()[0];

        // Replace or add video track
        const videoSender = senders.find(s => s.track?.kind === 'video');
        if (videoSender && videoTrack) {
          videoSender.replaceTrack(videoTrack);
          console.log(`[WebRTC] Replaced video track for ${remoteUserId}`);
        } else if (videoTrack) {
          peer._pc.addTrack(videoTrack, localStream);
          console.log(`[WebRTC] Added video track for ${remoteUserId}`);
        }

        // Replace or add audio track
        const audioSender = senders.find(s => s.track?.kind === 'audio');
        if (audioSender && audioTrack) {
          audioSender.replaceTrack(audioTrack);
          console.log(`[WebRTC] Replaced audio track for ${remoteUserId}`);
        } else if (audioTrack) {
          peer._pc.addTrack(audioTrack, localStream);
          console.log(`[WebRTC] Added audio track for ${remoteUserId}`);
        }
      } catch (error) {
        console.error(`[WebRTC] Error updating tracks for ${remoteUserId}:`, error);
      }
    });
  }, [localStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('[WebRTC] Component unmounting, cleaning up all connections');
      
      Object.keys(peersRef.current).forEach(peerId => {
        cleanupPeer(peerId);
      });
      
      processedSignalsRef.current.clear();
    };
  }, [cleanupPeer]);

  return { remoteStreams, peersRef };
};
